/**
 * Polls the server for a due check-in and shows the modal.
 *
 * The server owns the schedule; this component only asks "is anything due?".
 * Poll cadence adapts to how far away the next ping is, so an idle afternoon
 * costs a handful of requests rather than one every fifteen seconds.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { client } from '../api/client';
import type { PendingPing } from '../types';
import PingModal from './PingModal';
import {
  beep,
  bindAudioUnlock,
  notifyCheckIn,
  notificationState,
  requestNotifications,
} from '../lib/checkInAlarm';

const FAST_POLL_MS = 15_000;
const SLOW_POLL_MS = 60_000;

export default function PingWatcher() {
  const queryClient = useQueryClient();

  const { data } = useQuery<PendingPing>({
    queryKey: ['pending-ping'],
    queryFn: async () => (await client.get('/api/v1/attendance/ping/pending')).data,
    refetchInterval: (query) => {
      const result = query.state.data;
      if (!result?.sessionOpen) return false; // no shift, nothing to watch
      if (result.ping) return FAST_POLL_MS;

      const seconds = result.secondsUntilNext;
      if (seconds == null) return SLOW_POLL_MS;
      // Wake up shortly before the next ping is due, but never sleep so long
      // that a due prompt sits unnoticed.
      return Math.min(Math.max(seconds * 1000 * 0.5, FAST_POLL_MS), SLOW_POLL_MS);
    },
    // Keep polling while the tab is in the background. THIS is the fix.
    //
    // TanStack defaults it to false, and false does not mean "throttled" -- it
    // means interval refetching stops entirely once the tab loses focus. So
    // somebody with the app open behind another window was not polling slowly.
    // They were not polling. The ping fell due, the grace window closed, and
    // the sweep marked it missed without the prompt ever having rendered.
    //
    // On this policy that is four minutes of grace against ten-minute slots:
    // five minutes on another tab was enough to miss one, and the misses came
    // in runs of ten, twenty-five, fifty-four in a single day. 318 missed
    // against 23 answered was never a workforce ignoring prompts.
    //
    // The visibilitychange handler below is not a substitute for this. It
    // rescues somebody who comes back inside the window, and nobody else.
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  /**
   * Sound and notify on the edge, not on every poll.
   *
   * Once when the prompt appears, once more two minutes later if it is still
   * unanswered, then silence. The prompt cannot be dismissed and ignoring it
   * costs an availability score, so a single beep is too easy to miss — but a
   * beep every fifteen seconds is how somebody mutes the tab, which would kill
   * every signal at once.
   */
  const alarmed = useRef<string | null>(null);
  const repeat = useRef<number | null>(null);

  useEffect(() => {
    const pingId = data?.ping?.id ?? null;

    if (pingId === null) {
      alarmed.current = null;
      if (repeat.current) window.clearTimeout(repeat.current);
      repeat.current = null;
      return;
    }
    if (alarmed.current === pingId) return;

    alarmed.current = pingId;
    beep();
    notifyCheckIn();

    repeat.current = window.setTimeout(() => {
      // Only if this same prompt is still the one waiting.
      if (alarmed.current === pingId) {
        beep();
        notifyCheckIn();
      }
    }, 120_000);

    return () => {
      if (repeat.current) window.clearTimeout(repeat.current);
    };
  }, [data?.ping?.id]);

  // Buy the right to make a sound, from whatever the person does first.
  useEffect(() => bindAudioUnlock(), []);

  // A returning tab should learn about a waiting prompt immediately rather
  // than at the next scheduled poll.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [queryClient]);

  // Surface the prompt in the tab title so it is visible from another tab.
  useEffect(() => {
    const original = document.title;
    if (data?.ping) document.title = '● Check-in required — BNB India';
    return () => {
      document.title = original;
    };
  }, [data?.ping]);

  if (!data?.ping) {
    // Ask for notification permission where it is needed, not on a settings
    // page nobody opens.
    //
    // The toggle existed on My Profile, which is why permission sat at
    // "default" for almost everybody — and a default permission makes every
    // hidden-tab notification a silent no-op, which is exactly the case the
    // notification is for.
    //
    // Only during a shift, because that is when it means something, and only
    // while the answer is still "default": a denied permission cannot be
    // re-asked from script, and nagging is how somebody denies it for good.
    // requestPermission has to be called from a gesture, so it is a button.
    return <NotificationPrompt sessionOpen={Boolean(data?.sessionOpen)} />;
  }

  return (
    <PingModal
      ping={data.ping}
      onAnswered={() => {
        queryClient.invalidateQueries({ queryKey: ['pending-ping'] });
        queryClient.invalidateQueries({ queryKey: ['todays-pings'] });
        queryClient.invalidateQueries({ queryKey: ['my-attendance'] });
      }}
    />
  );
}

const PROMPT_DISMISSED = 'ats.checkInNotifyDismissed';

function NotificationPrompt({ sessionOpen }: { sessionOpen: boolean }) {
  const [state, setState] = useState(notificationState);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(PROMPT_DISMISSED) === 'yes',
  );

  if (!sessionOpen || dismissed || state !== 'default') return null;

  return (
    <div className="fixed bottom-4 right-4 z-30 max-w-sm rounded-lg border border-amber-300 bg-amber-50 p-3 shadow-lg">
      <p className="text-sm font-medium text-amber-900">
        Turn on check-in alerts
      </p>
      <p className="mt-0.5 text-xs text-amber-800">
        Your check-in can arrive while this tab is behind another window.
        Without permission there is nothing to tell you, and an unanswered
        check-in counts as missed.
      </p>
      <div className="mt-2 flex gap-2">
        <button
          type="button"
          className="btn-primary text-xs"
          onClick={async () => setState(await requestNotifications())}
        >
          Allow alerts
        </button>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => {
            localStorage.setItem(PROMPT_DISMISSED, 'yes');
            setDismissed(true);
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
